import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API_BASE, type MonthlyReport as MonthlyReportData } from '@/lib/api';
import { PLATFORMS } from '@/lib/platforms';
import { PlatformIcon } from '@/components/ui/PlatformIcon';
import { Loader2, Printer, TrendingUp, TrendingDown } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PublicReportPayload {
  report: MonthlyReportData;
  narrative: string | null;
  brand: {
    name: string;
    agency: string;
    logo_url: string | null;
    colors: string | null;
  };
}

const fmt = (n: number) => n.toLocaleString();

/**
 * White-label client report — token-gated public page, no agency login.
 * Print-friendly: the "Save as PDF" button drives the browser's print-to-PDF.
 */
export function PublicReport() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<PublicReportPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE}/api/public-report/${token}`)
      .then(async (resp) => {
        const body = (await resp.json()) as PublicReportPayload & { error?: string };
        if (!resp.ok) throw new Error(body.error ?? 'Report unavailable');
        setData(body);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Report unavailable'));
  }, [token]);

  // Brand accent from the workspace's first brand color (falls back to theme accent).
  let brandAccent: string | undefined;
  try {
    const colors = data?.brand.colors ? (JSON.parse(data.brand.colors) as string[]) : [];
    brandAccent = colors[0];
  } catch {
    /* keep theme accent */
  }

  if (error) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center p-6">
        <p className="text-sm text-muted-foreground">{error}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const { report, narrative, brand } = data;
  const monthLabel = new Date(`${report.month}-01T00:00:00Z`).toLocaleString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
  const tiles = [
    { label: 'Posts Published', value: report.publishedCount },
    { label: 'Impressions', value: report.totals.impressions },
    { label: 'Engagements', value: report.totals.engagements },
    { label: 'Likes', value: report.totals.likes },
    { label: 'Comments', value: report.totals.comments },
    { label: 'Shares', value: report.totals.shares },
  ];

  return (
    <div className="min-h-screen bg-canvas print:bg-white">
      <style>{`@media print { .no-print { display: none !important; } body { background: white; } }`}</style>
      <div className="max-w-4xl mx-auto p-6 lg:p-10 space-y-8">
        {/* Branded header */}
        <header className="flex items-center justify-between gap-4 flex-wrap border-b border-hairline pb-6">
          <div className="flex items-center gap-4">
            {brand.logo_url && (
              <img
                src={brand.logo_url}
                alt={`${brand.name} logo`}
                className="h-12 w-12 rounded-m object-contain"
              />
            )}
            <div>
              <h1
                className="font-display text-2xl font-semibold text-foreground"
                style={brandAccent ? { color: brandAccent } : undefined}
              >
                {brand.name}
              </h1>
              <p className="text-sm text-muted-foreground">
                Social Media Report · {monthLabel}
              </p>
            </div>
          </div>
          <button
            onClick={() => window.print()}
            className="no-print flex items-center gap-2 px-4 py-2 text-sm font-medium bg-accent text-accent-foreground rounded-m hover:bg-accent/90 transition-colors"
          >
            <Printer className="h-4 w-4" />
            Save as PDF
          </button>
        </header>

        {/* Headline numbers */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {tiles.map((t) => (
            <div key={t.label} className="bg-card rounded-l p-4 ring-1 ring-hairline">
              <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                {t.label}
              </p>
              <p className="font-display text-2xl font-semibold text-foreground mt-1 tabular-nums">
                {fmt(t.value)}
              </p>
            </div>
          ))}
        </div>

        {/* Narrative */}
        {narrative && (
          <section className="bg-card rounded-l ring-1 ring-hairline p-6">
            <h2 className="font-display text-base font-semibold text-foreground mb-3">
              The story of {monthLabel}
            </h2>
            <div className="text-sm text-foreground/90 leading-relaxed space-y-3">
              {narrative.split(/\n\n+/).map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </section>
        )}

        {/* Growth + platforms */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <section className="bg-card rounded-l ring-1 ring-hairline p-5">
            <h3 className="font-display text-sm font-semibold text-foreground mb-3">
              Follower growth
            </h3>
            {report.followerGrowth.length === 0 ? (
              <p className="text-xs text-muted-foreground">Growth data begins next month.</p>
            ) : (
              <div className="space-y-2.5">
                {report.followerGrowth.map((g) => (
                  <div key={g.platform} className="flex items-center gap-3">
                    <span className="flex items-center gap-1.5 text-xs text-foreground w-28 shrink-0">
                      <PlatformIcon platform={g.platform} variant="glyph" size="xs" />
                      {PLATFORMS[g.platform as keyof typeof PLATFORMS]?.label ?? g.platform}
                    </span>
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {fmt(g.start)} → {fmt(g.end)}
                    </span>
                    <span
                      className={cn(
                        'flex items-center gap-0.5 text-xs font-semibold tabular-nums ml-auto',
                        g.delta >= 0 ? 'text-success' : 'text-destructive'
                      )}
                    >
                      {g.delta >= 0 ? (
                        <TrendingUp className="h-3 w-3" />
                      ) : (
                        <TrendingDown className="h-3 w-3" />
                      )}
                      {g.delta >= 0 ? '+' : ''}
                      {fmt(g.delta)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="bg-card rounded-l ring-1 ring-hairline p-5">
            <h3 className="font-display text-sm font-semibold text-foreground mb-3">By platform</h3>
            {report.platforms.length === 0 ? (
              <p className="text-xs text-muted-foreground">No platform metrics this month.</p>
            ) : (
              <div className="space-y-2.5">
                {report.platforms.map((p) => (
                  <div key={p.platform} className="flex items-center gap-3 text-xs">
                    <span className="flex items-center gap-1.5 text-foreground w-28 shrink-0">
                      <PlatformIcon platform={p.platform} variant="glyph" size="xs" />
                      {PLATFORMS[p.platform as keyof typeof PLATFORMS]?.label ?? p.platform}
                    </span>
                    <span className="text-muted-foreground tabular-nums">
                      {p.posts} posts · {fmt(p.impressions)} impressions · {fmt(p.engagements)} engagements
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* Best performers */}
        <section className="bg-card rounded-l ring-1 ring-hairline p-5">
          <h3 className="font-display text-sm font-semibold text-foreground mb-3">
            Best performing posts
          </h3>
          {report.topPosts.length === 0 ? (
            <p className="text-xs text-muted-foreground">No ranked posts this month.</p>
          ) : (
            <div className="space-y-2">
              {report.topPosts.map((p, i) => (
                <div key={p.id} className="flex items-start gap-3 p-3 bg-muted/40 ring-1 ring-border/50 rounded-lg">
                  <span className="text-sm font-bold text-muted-foreground w-6 shrink-0">#{i + 1}</span>
                  <p className="text-xs text-foreground line-clamp-2 flex-1 min-w-0">
                    {p.content || <span className="italic text-muted-foreground">(no caption)</span>}
                  </p>
                  <span className="text-xs text-muted-foreground shrink-0 tabular-nums">
                    {fmt(p.engagements)} eng · {fmt(p.impressions)} impr
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* What's next */}
        {report.upcoming.length > 0 && (
          <section className="bg-card rounded-l ring-1 ring-hairline p-5">
            <h3 className="font-display text-sm font-semibold text-foreground mb-3">Coming up next</h3>
            <div className="space-y-2">
              {report.upcoming.map((p) => (
                <div key={p.id} className="flex items-center gap-3 text-xs">
                  <span className="text-muted-foreground shrink-0 tabular-nums w-24">
                    {new Date(p.scheduled_at).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                    })}
                  </span>
                  <p className="text-foreground line-clamp-1 flex-1 min-w-0">{p.content}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        <footer className="text-center text-[11px] text-muted-foreground pt-4 border-t border-hairline">
          Prepared by {brand.agency}
        </footer>
      </div>
    </div>
  );
}
